import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import classnames from 'classnames/bind';
import { withRouter } from 'react-router-dom';
import { Table, Icon, Row, Col } from 'grab-ui';
import { trackClick, event, cat, custom } from 'common/utils/instrumentation';

import TableActionColumn from 'components/v2/table-action-column/table-action-column';
import OrderStatus from 'components/v2/order-status/order-status';
import CustomerInfo from 'components/v2/customer-info/customer-info';
import DivButton from 'components/v2/div-button/div-button';
import { reverseOrderPOIs } from 'helper/address-search';
import { pushGAPageViewEvent } from 'helper/ga-helper';
import {
  checkBookAgainable,
  getCreatedTimeToRender,
  getReceiptRecipients,
} from 'helper/history-order';
import { getUserCountryCode, getCompanyID } from 'helper/user';
import {
  getAmountDisplay,
  getOrderItemWeight,
  getSenderAndRecipientInfo,
  isShopifyOrder,
} from 'helper/order';
import { paymentMethodForDisplay } from 'helper/payment';
import { ROUTES_V2_PATHS } from 'constants/route-constants';
import { HELP_CENTER } from 'constants/app-constants';
import { MODE } from 'views/v2/home/constants';
import cssTable from 'components/v2/ongoing-bookings-section/ongoing-bookings-section.less';
import OrderPaymentDetails from 'components/v2/order-payment-details';
import {
  scribeTrackEvent,
  getCommonFacet,
  SCRIBE_STATE_NAMES,
  SCRIBE_BOOKING_EVENTS,
  SCRIBE_EVENT_PARAMS,
} from 'applications/scribe/instrumentations/track';
import {
  enableAnytimeBookingUIBookingSelector,
  isPaxFromUserInfo,
} from 'selectors/app-selector';
import NewBookingDuck from 'ducks/new-booking-duck';
import ScribeDuck from 'ducks/scribe-duck';

import css from './order-table-complex.less';
import ReceiptDownload from './receipt-download/receipt-download';

const cx = classnames.bind(css);

const getActionDropdownID = order => `ant-dropdown-${order.deliveryID}`;

class OrderTableComplex extends Component {
  constructor(props) {
    super(props);
    const { userInfo } = this.props;
    const companyID = getCompanyID(userInfo);
    this.state = {};
    this.columns = [
      {
        title: I18n.t('v2.orderTable.column.date'),
        key: 'date',
        render: order => getCreatedTimeToRender(order),
        width: '15%',
      },
      {
        title: I18n.t('v2.orderTable.column.sender'),
        dataIndex: 'steps',
        key: 'sender',
        render: steps => _get(steps, '[0].contact.name'),
        width: '10%',
      },
      {
        title: I18n.t('v2.orderTable.column.recipient'),
        dataIndex: 'steps',
        key: 'recipient',
        render: steps => _get(steps, '[1].contact.name'),
        width: '10%',
      },
      {
        title: I18n.t('v2.orderTable.column.deliveryType'),
        dataIndex: 'service',
        key: 'service',
        render: service => _get(service, 'serviceName'),
        width: '15%',
      },
      {
        title: I18n.t('v2.orderTable.column.price'),
        dataIndex: 'amount',
        key: 'price',
        render: (amount, order) =>
          getAmountDisplay(amount, order.currency, false),
        width: '15%',
      },
      {
        title: I18n.t('v2.orderTable.column.orderState'),
        key: 'status',
        render: order => <OrderStatus order={order} />,
        width: '25%',
      },
      {
        title: '',
        key: 'action',
        width: '5%',
        render: order => {
          const ReceiptDownloadComponent = (
            <ReceiptDownload order={order} simpleDownloadBtn />
          );
          const dropdownItems = [
            {
              value: ReceiptDownloadComponent,
              disabled: _isEmpty(getReceiptRecipients(order)),
            },
            {
              value: I18n.t('v2.orderTable.action.bookAgain'),
              disabled: !checkBookAgainable(order),
              onClick: this.onClickBookAgain(order),
            },
            {
              value: I18n.t('v2.orderTable.action.reportIssue'),
              onClick: this.onClickReportIssue(order),
            },
          ];
          return (
            <DivButton onClick={this.onClickStopPropagation}>
              <TableActionColumn
                id={getActionDropdownID(order)}
                items={dropdownItems}
                buttonClick={() => {
                  trackClick(
                    event.BOOKINGS_CLICKS,
                    cat.GE_BOOKINGS,
                    'PAST_BOOKINGS_DETAILS',
                    {
                      [custom.COMPANY_ID]: companyID,
                      ORDER_ID: order.deliveryID,
                    },
                  );

                  scribeTrackEvent({
                    stateName: SCRIBE_STATE_NAMES.EXPRESS_BOOKINGS_WEB,
                    eventName: SCRIBE_BOOKING_EVENTS.PAST_BOOKINGS_DETAILS,
                    facets: {
                      ...getCommonFacet(userInfo),
                      [SCRIBE_EVENT_PARAMS.ORDER_ID]: order.deliveryID,
                      [SCRIBE_EVENT_PARAMS.BOOKING_CODE]: order.bookingCode,
                    },
                  });
                }}
              />
            </DivButton>
          );
        },
      },
    ];
  }

  // eslint-disable-next-line react/sort-comp
  hideActionDropdown = order => {
    const id = getActionDropdownID(order);
    try {
      const actionDropdown = document.getElementById(id);
      actionDropdown.click();
    } catch (e) {
      // do nothing
    }
  };

  onClickBookAgain = (order, needHideActionDropdown = true) => async () => {
    const {
      userInfo,
      updateOrderShallow,
      bookAgain,
      shopifyBookAgain,
      history,
      enableAnytimeBookingUIBooking,
      anytimeBookingBookAgain,
      scribeExpressBookingsWebBookAgain,
    } = this.props;
    const shopifyOrder = isShopifyOrder(_get(order, 'thirdPartySource'));
    if (needHideActionDropdown) {
      this.hideActionDropdown(order);
    }
    scribeExpressBookingsWebBookAgain(order);

    if (enableAnytimeBookingUIBooking && !shopifyOrder) {
      anytimeBookingBookAgain(order, () => {
        history.push(ROUTES_V2_PATHS.newBooking);
      });
      return;
    }

    updateOrderShallow({});

    const companyID = getCompanyID(userInfo);
    const isPax = isPaxFromUserInfo(userInfo);
    const pois = await reverseOrderPOIs(order, companyID, isPax);

    if (shopifyOrder) {
      shopifyBookAgain({
        orders: _get(order, 'shopifyMetaData').map(({ orderID, step_idx }) => ({
          orderID,
          address: _get(pois, `[${step_idx}]`),
        })),
      });
      history.push({
        pathname: ROUTES_V2_PATHS.shopifyHome,
      });
      return;
    }

    setTimeout(() => {
      bookAgain({ order, pois });
    }, 200);

    history.push({
      pathname: ROUTES_V2_PATHS.home,
      state: {
        mode: MODE.SINGLE_BOOK,
      },
    });
  };

  onClickReportIssue = order => () => {
    const { userInfo } = this.props;
    this.hideActionDropdown(order);

    const countryCode = getUserCountryCode(userInfo);
    const helpCenterLink = HELP_CENTER[countryCode] || HELP_CENTER.SG;

    pushGAPageViewEvent('HELP', userInfo);
    window.open(helpCenterLink);
  };

  onClickStopPropagation = e => {
    // prevent to expand column
    e.stopPropagation();
  };

  renderExpandIcon = args => {
    const icon = _get(args, 'expanded') ? 'down' : 'right';
    return <Icon type={icon} />;
  };

  renderExpandedOrder = order => {
    const { userInfo } = this.props;
    const { countryCode } = userInfo.selectedCity;
    const { sender, recipients } = getSenderAndRecipientInfo(order);

    const disableBookAgainBtn = !checkBookAgainable(order);
    const promoCode = _get(order, 'service.promoCode');
    const referenceCode = _get(order, 'referenceCode');
    const isShopify = isShopifyOrder(_get(order, 'thirdPartySource'));
    const shopifyMetaDatas = _get(order, 'shopifyMetaData', []);
    const isMSD = _get(order, 'steps.length') > 2;
    const orderPaymentDetails = _get(order, 'paymentMethods', []);

    return (
      <Row type="flex" className={css.expandedOrder}>
        <Col xs={8} className={css.leftPanel}>
          <CustomerInfo info={sender} isSender isViewMode />
          {recipients.map((r, index, allItems) => (
            <CustomerInfo
              key={`${r.keywords}-${r.phone}`}
              info={r}
              isSender={false}
              isViewMode
              index={index}
              noOfRecipients={allItems.length}
              countryCode={countryCode}
              isShopifyOrder={isShopify}
              shopifyMetaDatas={shopifyMetaDatas.filter(
                shopifyMetaData => shopifyMetaData.step_idx === index + 1,
              )}
            />
          ))}
        </Col>
        <Col xs={16} className={css.rightPanel}>
          <Row type="flex" gutter={{ md: 60 }}>
            <Col xs={12} md={6}>
              <h6>{I18n.t('v2.orderTable.column.payment')}</h6>
              {orderPaymentDetails.length > 1 ||
              !_get(order, 'paymentMethod.type') ? (
                <OrderPaymentDetails
                  orderPaymentDetails={orderPaymentDetails}
                />
              ) : (
                <h5>
                  {paymentMethodForDisplay(
                    _get(order, 'paymentMethod', {}),
                    true,
                    countryCode,
                  )}
                </h5>
              )}
            </Col>
            {promoCode && (
              <Col xs={12} md={6}>
                <h6>{I18n.t('v2.orderTable.column.promo')}</h6>
                <h5>{promoCode}</h5>
              </Col>
            )}
            <Col xs={12} md={6}>
              <h6>{I18n.t('v2.orderTable.column.bookingID')}</h6>
              <h5>
                {isMSD
                  ? _get(order, 'bookingCode', '--')
                  : _get(order, 'orderID', '--')}
              </h5>
            </Col>
            {referenceCode && (
              <Col xs={12} md={6}>
                <h6>{I18n.t('v2.orderTable.column.referenceCode')}</h6>
                <h5>{referenceCode}</h5>
              </Col>
            )}
            <Col xs={12} md={6}>
              <h6>{I18n.t('v2.orderTable.column.orderType.label')}</h6>
              <h5>
                {I18n.t(
                  `v2.orderTable.column.orderType.source.${_get(
                    order,
                    'source',
                    0,
                  )}`,
                )}
              </h5>
            </Col>
          </Row>
          <Row type="flex" gutter={{ md: 60 }}>
            <Col xs={12} md={6}>
              <h6>{I18n.t('v2.orderTable.column.itemType')}</h6>
              <h5>{_get(order, 'steps[1].itemInfo.categoryName', '--')}</h5>
            </Col>
            <Col xs={12} md={6}>
              <h6>{I18n.t('v2.orderTable.column.weight')}</h6>
              <h5>{getOrderItemWeight(order)}</h5>
            </Col>
            <Col xs={24} md={12}>
              <h6>{I18n.t('v2.orderTable.column.instruction')}</h6>
              <h5 className={css.instruction}>
                {_get(order, 'noteToDriver', '--')}
              </h5>
            </Col>
          </Row>
          <Row type="flex" className={css.actionRow}>
            <ReceiptDownload order={order} />
            <span className={css.divider}>|</span>
            <DivButton
              onClick={this.onClickBookAgain(order, false)}
              className={cx('divButton', {
                divButtonDisable: disableBookAgainBtn,
              })}
            >
              {I18n.t('v2.orderTable.action.bookAgain')}
            </DivButton>
            <span className={css.divider}>|</span>
            <DivButton
              onClick={this.onClickReportIssue(order)}
              className={css.divButton}
            >
              {I18n.t('v2.orderTable.action.reportIssue')}
            </DivButton>
          </Row>
        </Col>
      </Row>
    );
  };

  render() {
    const { orders, className, pagination, loading, scroll } = this.props;

    return (
      <Table
        columns={this.columns}
        dataSource={orders}
        className={cx(cssTable.table, 'table', className, {
          'table-empty': _isEmpty(orders),
        })}
        pagination={pagination}
        expandedRowRender={this.renderExpandedOrder}
        expandIcon={this.renderExpandIcon}
        expandRowByClick
        loading={loading}
        scroll={scroll}
        rowKey="deliveryID"
      />
    );
  }
}

OrderTableComplex.propTypes = {
  userInfo: PropTypes.shape({}).isRequired,
  orders: PropTypes.arrayOf(PropTypes.shape({})),
  className: PropTypes.string,
  pagination: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.bool]),
  updateOrderShallow: PropTypes.func.isRequired,
  bookAgain: PropTypes.func.isRequired,
  shopifyBookAgain: PropTypes.func.isRequired,
  anytimeBookingBookAgain: PropTypes.func.isRequired,
  scribeExpressBookingsWebBookAgain: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  scroll: PropTypes.shape({}).isRequired,
  enableAnytimeBookingUIBooking: PropTypes.bool,
};

OrderTableComplex.defaultProps = {
  orders: [],
  className: '',
  pagination: {},
  scroll: {},
  enableAnytimeBookingUIBooking: false,
};

export default withRouter(
  connect(
    state => ({
      enableAnytimeBookingUIBooking: enableAnytimeBookingUIBookingSelector(
        state,
      ),
    }),
    dispatch => ({
      anytimeBookingBookAgain: (order, callback) =>
        dispatch(NewBookingDuck.creators.rebook(order, callback)),
      scribeExpressBookingsWebBookAgain: order =>
        dispatch(ScribeDuck.creators.scribeExpressBookingsWebBookAgain(order)),
    }),
  )(OrderTableComplex),
);
